import React, { useState, useEffect } from 'react';
import { getAllPlatforms } from '../platform/controller/platform-controller';
import DepotController from '../controllers/depot-controller';

const GenerateLinkModal = ({ isOpen, onClose, selectedProducts }) => {
  const [platforms, setPlatforms] = useState([]);
  const [depots, setDepots] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedDepot, setSelectedDepot] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(0);
  
  // Fetch platforms when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPlatforms();
      fetchDepots();
      setSelectedProductIndex(0); // Reset to first product when modal opens
    }
  }, [isOpen]);
  
  const fetchPlatforms = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAllPlatforms();
      setPlatforms(data || []);
    } catch (err) {
      console.error('Error fetching platforms:', err);
      setError('Impossible de charger les plateformes. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepots = async () => {
    try {
      const data = await DepotController.getAllDepots();
      setDepots(data || []);
      const def = (data || []).find((d) => d.isDefault);
      if (def) setSelectedDepot(String(def.id));
    } catch (err) {
      console.error('Error fetching depots:', err);
    }
  };
  
  const handlePlatformChange = (e) => {
    setSelectedPlatform(e.target.value);
    setGeneratedLink(''); // Clear previous link when platform changes
    setIsCopied(false);
  };

  const handleDepotChange = (e) => {
    setSelectedDepot(e.target.value);
    setGeneratedLink('');
    setIsCopied(false);
  };
  
  const handleProductChange = (e) => {
    setSelectedProductIndex(parseInt(e.target.value));
    setGeneratedLink(''); // Clear previous link when product changes
    setIsCopied(false);
  };
  
  const generateLink = () => {
    if (!selectedPlatform || !selectedDepot || selectedProducts.length === 0) return;
    
    const url = new URL(window.location.href);
    const baseUrl = url.origin + url.pathname.split('/').slice(0, 1).join('/');
    
    // Use the product at the selected index
    const productId = selectedProducts[selectedProductIndex];
    
    const link = `${baseUrl}/product/${productId}/${selectedPlatform}/${selectedDepot}`;
    setGeneratedLink(link);
    setIsCopied(false);
  };
  
  const copyToClipboard = () => {
    if (!generatedLink) return;
    
    // Try using the Clipboard API first (works in secure contexts)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(generatedLink)
        .then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 3000);
        })
        .catch(err => {
          console.error('Failed to copy with Clipboard API:', err);
          // Fall back to the alternative method if Clipboard API fails
          fallbackCopyToClipboard();
        });
    } else {
      // Use fallback method if Clipboard API isn't available
      fallbackCopyToClipboard();
    }
  };
  
  // Fallback method using document.execCommand
  const fallbackCopyToClipboard = () => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement("textarea");
      textArea.value = generatedLink;
      
      // Set styles to make it invisible
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      
      // Select the text and copy it
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      
      // Clean up
      document.body.removeChild(textArea);
      
      if (successful) {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      } else {
        console.error('Fallback copy method failed');
      }
    } catch (err) {
      console.error('Fallback copy method failed:', err);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Générer un lien
                </h3>
                
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
                    {error}
                  </div>
                )}
                
                {/* Product selector - only show if multiple products are selected */}
                {selectedProducts.length > 1 && (
                  <div className="mt-4">
                    <label htmlFor="product" className="block text-sm font-medium text-gray-700 mb-1">
                      Sélectionnez un produit
                    </label>
                    <select
                      id="product"
                      name="product"
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
                      value={selectedProductIndex}
                      onChange={handleProductChange}
                    >
                      {selectedProducts.map((productId, index) => (
                        <option key={productId} value={index}>
                          Produit ID: {productId}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="mt-4">
                  <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-1">
                    Sélectionnez une plateforme
                  </label>
                  <select
                    id="platform"
                    name="platform"
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
                    value={selectedPlatform}
                    onChange={handlePlatformChange}
                    disabled={isLoading}
                  >
                    <option value="">Sélectionner une plateforme</option>
                    {platforms.map(platform => (
                      <option key={platform.id} value={platform.id}>
                        {platform.name} {platform.typePlateform ? ` (${platform.typePlateform})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="depot" className="block text-sm font-medium text-gray-700 mb-1">
                    Sélectionnez un dépôt <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="depot"
                    name="depot"
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
                    value={selectedDepot}
                    onChange={handleDepotChange}
                    required
                  >
                    <option value="">Sélectionner un dépôt</option>
                    {depots.map(depot => (
                      <option key={depot.id} value={depot.id}>
                        {depot.name}{depot.isDefault ? ' (défaut)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                {generatedLink && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lien généré
                    </label>
                    <div className="flex mt-1">
                      <input
                        type="text"
                        className="flex-1 block w-full px-3 py-2 sm:text-sm border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-[#174540] focus:border-[#174540]"
                        value={generatedLink}
                        readOnly
                      />
                      <button
                        type="button"
                        onClick={copyToClipboard}
                        className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-r-md text-white ${isCopied ? 'bg-green-600' : 'bg-[#174540] hover:bg-[#0d2926]'} focus:outline-none`}
                      >
                        {isCopied ? 'Copié' : 'Copier'}
                      </button>
                    </div>
                    {selectedProducts.length > 1 && (
                      <p className="mt-2 text-xs text-gray-500">
                        Ce lien est généré pour le produit ID: {selectedProducts[selectedProductIndex]}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#174540] text-base font-medium text-white hover:bg-[#0d2926] focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
              onClick={generateLink}
              disabled={!selectedPlatform || !selectedDepot || selectedProducts.length === 0 || isLoading}
            >
              Générer
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateLinkModal;